import React, { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getPublicProject } from "../Projects/services/projectCrud";
import { getPublicTree } from "../Trees/services/treeCrud";
import useTreeRotation from "./hooks/useSlideRotation";
import { BrandingBarTop, BrandingBarBottom } from "./components/BrandingBar";
import TreeFullPage from "./components/TreeFullPage";
import "./ExternalDisplay.scss";

export default function ExternalDisplay() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const interval = parseInt(searchParams.get("interval") || "30000", 10);

  // Fetch project data (includes areas with tree_set + weather)
  const { data: project, isLoading: projectLoading } = useQuery(
    ["display", "project", projectId],
    () => getPublicProject(projectId),
    {
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );

  // Flatten trees from all areas: project.areas[].tree_set[]
  const trees = useMemo(() => {
    if (!project?.areas) return [];
    return project.areas.flatMap((area) => area.tree_set || []);
  }, [project]);
  const treeCount = trees.length || 1;
  const { currentTreeIndex, goToNext, goToPrev, goToIndex } = useTreeRotation(treeCount, interval);
  const currentTreeId = trees[currentTreeIndex]?.id;

  // Fetch detailed tree data for the current tree
  const { data: tree } = useQuery(
    ["display", "tree", currentTreeId],
    () => getPublicTree(currentTreeId),
    {
      enabled: !!currentTreeId,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );

  // Show loading only during initial fetch (not retries)
  if (!project && projectLoading) {
    return <div className="display-loading">Daten werden geladen...</div>;
  }

  const weather = project?.weather;
  const treeName = tree?.name || trees[currentTreeIndex]?.name || "Baum";
  const speciesName = tree?.species?.de_name || tree?.species?.name || "";

  return (
    <div className="external-display">
      <div className="external-display__frame">
        <BrandingBarTop
          treeName={treeName}
          speciesName={speciesName}
          treeIndex={currentTreeIndex}
          treeCount={treeCount}
        />

        <div className="tree-page-container">
          <div className="tree-page-wrapper tree-page-wrapper--active">
            <TreeFullPage tree={tree} weather={weather} />
          </div>
        </div>

        <BrandingBarBottom
          currentTreeIndex={currentTreeIndex}
          treeCount={treeCount}
          onNext={goToNext}
          onPrev={goToPrev}
          onGoTo={goToIndex}
        />
      </div>
    </div>
  );
}
